<script setup lang="ts">
import type { ResponsiveHeaderAction, ResponsiveHeaderMode } from '@hprofits/ui';
import {
  ArrowLeftIcon,
  Badge,
  Breadcrumbs,
  Button,
  CogIcon,
  HamburgerIcon,
  PageShellHeader,
  PageShellHeaderLeft,
  PageShellHeaderRight,
  ResponsiveHeaderActions,
  ResponsiveHeaderItem,
  SaveCrossedIcon,
  SaveIcon,
  Text,
  TrashIcon,
} from '@hprofits/ui';
import { isEmpty } from '@hprofits/utils/fp/optional';
import { computed, onMounted, onUnmounted } from 'vue';
import { RouterLink } from 'vue-router';

import { t } from '@/i18n';
import { useCampaignGroupContext } from '@/modules/campaigns/composables/context';
import { useCampaignOwnerInfo } from '@/modules/campaigns/composables/useCampaignOwnerInfo';
import { useUnsavedChangesDialog } from '@/modules/campaigns/composables/useUnsavedChangesDialog';
import { HeaderProfileMenu, HeaderThemeToggle } from '@/modules/shared/components';

import { useCampaignBreadcrumbs } from './composables';

type Props = Readonly<{
  setMobileMenuOpen: () => void;
}>;

const props = defineProps<Props>();

type HeaderResponsiveSlot = 'campaignMenu' | 'breadcrumbs' | 'minimalBreadcrumbs' | 'status' | 'owner';
type HeaderResponsiveConfig = Readonly<Record<HeaderResponsiveSlot, ReadonlyArray<ResponsiveHeaderMode>>>;

const HEADER_RESPONSIVE_CONFIG: HeaderResponsiveConfig = {
  campaignMenu: ['compact', 'minimal'],
  breadcrumbs: ['wide', 'compact'],
  minimalBreadcrumbs: ['minimal'],
  status: ['wide'],
  owner: ['wide'],
};

const {
  params: { campaignGroupId },
  campaignGroupSource: {
    state: { data: campaignGroupData },
  },
  state: { hasLoading, hasLoadingOrPending, canSubmit },
  actions: { deleteCampaignGroup, discardChanges, submit },
} = useCampaignGroupContext();

const { confirm } = useUnsavedChangesDialog();

const breadcrumbsResult = useCampaignBreadcrumbs({
  campaignGroupId,
  campaignGroupData,
});

const { ownerId, ownerEmail } = useCampaignOwnerInfo(campaignGroupData);
const minimalBreadcrumbs = computed(() => breadcrumbsResult.value.breadcrumbs?.slice(0, 1) ?? []);

const handleSaveShortcut = (event: KeyboardEvent): void => {
  if ((event.ctrlKey || event.metaKey) && event.key === 's' && canSubmit.value) {
    event.preventDefault();
    void submit();
  }
};

const handleClearChanges = async (): Promise<void> => {
  if (!canSubmit.value || hasLoadingOrPending.value) {
    return;
  }

  const result = await confirm({
    title: t('modules.campaigns.layout.header.clearChangesTitle'),
    description: t('modules.campaigns.layout.header.clearChangesDescription'),
    leaveText: t('modules.campaigns.layout.header.clearChangesConfirm'),
    stayText: t('modules.campaigns.layout.header.clearChangesCancel'),
  });

  if (result === 'leave') {
    discardChanges();
  }
};

const headerActions = computed<ReadonlyArray<ResponsiveHeaderAction>>(() => [
  {
    id: 'clear-changes',
    label: t('modules.campaigns.layout.header.clearChanges'),
    icon: SaveCrossedIcon,
    presentation: {
      wide: 'button',
      compact: 'menu',
      minimal: 'menu',
    },
    variant: 'outline',
    disabled: !canSubmit.value || hasLoadingOrPending.value,
    onClick: handleClearChanges,
  },
  {
    id: 'save',
    label: t('modules.campaigns.layout.header.save'),
    icon: SaveIcon,
    presentation: {
      wide: 'button',
      compact: 'button',
      minimal: 'icon',
    },
    variant: canSubmit.value ? 'filled' : 'soft',
    disabled: !canSubmit.value,
    loading: hasLoadingOrPending.value,
    onClick: async () => {
      await submit();
    },
  },
  {
    id: 'delete-campaign',
    label: t('modules.campaigns.layout.header.deleteCampaign'),
    icon: TrashIcon,
    presentation: {
      wide: 'menu',
      compact: 'menu',
      minimal: 'menu',
    },
    menuVariant: 'danger',
    disabled: hasLoadingOrPending.value,
    onClick: deleteCampaignGroup,
  },
]);

onMounted(() => {
  window.addEventListener('keydown', handleSaveShortcut);
});
onUnmounted(() => {
  window.removeEventListener('keydown', handleSaveShortcut);
});
</script>

<template>
  <PageShellHeader>
    <PageShellHeaderLeft>
      <ResponsiveHeaderItem :modes="HEADER_RESPONSIVE_CONFIG.campaignMenu">
        <Button
          variant="outline"
          size="3"
          :icon="HamburgerIcon"
          :aria-label="$t('modules.campaigns.layout.menu.openCampaignMenu')"
          radius="full"
          square
          @click="props.setMobileMenuOpen"
        />
      </ResponsiveHeaderItem>
      <Button
        :as="RouterLink"
        :to="breadcrumbsResult.backRoute"
        variant="blank"
        size="3"
        :icon="ArrowLeftIcon"
        :aria-label="$t('modules.campaigns.layout.header.back')"
        square
      />
      <ResponsiveHeaderItem :modes="HEADER_RESPONSIVE_CONFIG.breadcrumbs">
        <Breadcrumbs
          :items="breadcrumbsResult.breadcrumbs ?? []"
          :loading="hasLoading && isEmpty(breadcrumbsResult.breadcrumbs)"
        />
      </ResponsiveHeaderItem>
      <ResponsiveHeaderItem :modes="HEADER_RESPONSIVE_CONFIG.minimalBreadcrumbs">
        <Breadcrumbs :items="minimalBreadcrumbs" :loading="hasLoading && isEmpty(breadcrumbsResult.breadcrumbs)" />
      </ResponsiveHeaderItem>
    </PageShellHeaderLeft>
    <PageShellHeaderRight>
      <ResponsiveHeaderItem :modes="HEADER_RESPONSIVE_CONFIG.status">
        <Transition name="fade">
          <Badge v-if="canSubmit" class="unsaved-alert" size="4" variant="soft" color="yellow" :icon="SaveIcon">
            {{ $t('modules.campaigns.layout.header.unsavedChanges') }}
          </Badge>
        </Transition>
      </ResponsiveHeaderItem>
      <ResponsiveHeaderItem v-if="ownerEmail" :modes="HEADER_RESPONSIVE_CONFIG.owner">
        <Text v-if="!ownerId" size="3" variant="soft">
          {{ ownerEmail }}
        </Text>
        <Text
          v-else
          size="3"
          variant="soft"
          :as="RouterLink"
          :to="{ name: 'profile-admin', params: { userId: ownerId } }"
          link
        >
          {{ ownerEmail }}
        </Text>
      </ResponsiveHeaderItem>
      <ResponsiveHeaderActions :actions="headerActions" :menu-icon="CogIcon" menu-variant="outline" />
      <HeaderThemeToggle />
      <HeaderProfileMenu :campaign-owner-email="ownerEmail" :campaign-owner-id="ownerId" />
    </PageShellHeaderRight>
  </PageShellHeader>
</template>
